// SigninAnnouncementSelect, not a row with relations: the admin route returns the
// bare rows without the updatedByUser relation.
import type { SigninAnnouncementSelect, PublicSigninAnnouncement } from 'types';
import type { SigninReply } from 'definitions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';

export type SigninAnnouncementFormValues = {
  active: boolean;
  publishedAt: number;
  intro: string;
  headline: string;
  body: string;
  replies: SigninReply[];
};

/**
 * Reads the public route, so this works on the auth screens where there is no
 * session yet.
 *
 * Deliberately a bare fetch rather than useClient, for the same reason
 * useAnnouncement is: useClient routes every response over 400 into the global
 * API error dialog, and an error dialog stacked on the login screen because a
 * marketing panel could not load is worse than the evergreen copy.
 *
 * No polling. The announcement bar polls because it carries operational notices
 * that matter within minutes; nobody sits on the login screen waiting for the chat
 * to change.
 *
 * `isReady` is false until the request settles. The chat starts playing the moment
 * it mounts, so it has to wait for the answer: starting on the evergreen copy and
 * swapping to the published announcements a moment later would restart the
 * conversation under the reader.
 */
export const useSigninAnnouncements = () => {
  const { data, isPending } = useQuery<PublicSigninAnnouncement[]>({
    queryKey: ['SIGNIN_ANNOUNCEMENTS'],
    queryFn: async () => {
      try {
        // A timeout, because the chat waits for this before it says anything: a request
        // that hangs would leave the panel blank instead of playing the standing copy.
        const res = await fetch(`${config.API_URL}/signin-panel/active`, { signal: AbortSignal.timeout(4000) });
        if (!res.ok) return [];
        const body: unknown = await res.json();
        // Not trusted to be a list. The SPA is built separately from the server
        // restart, so for a while after a deploy this can be answered by the
        // previous API, which served one object or null. A crash on the login
        // screen is the worst way to find out the two are out of step.
        return Array.isArray(body) ? (body as PublicSigninAnnouncement[]) : [];
      } catch {
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
  });

  return { announcements: data ?? [], isReady: !isPending };
};

type SaveParams = { id: number | null; values: SigninAnnouncementFormValues };

/**
 * The Super Admin editing surface. Unlike the public read this returns every row
 * whatever its state, because editing unpublished copy and finding an old
 * announcement to republish is the point.
 */
export const useSigninAnnouncementsAdmin = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<SigninAnnouncementSelect[]>({
    queryKey: ['SIGNIN_ANNOUNCEMENTS_ADMIN'],
    queryFn: () => client.get<SigninAnnouncementSelect[]>('/signin-panel'),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['SIGNIN_ANNOUNCEMENTS_ADMIN'] });
    // Drops the app's own copy of the public list. It does not defeat the HTTP cache:
    // the route is sent with max-age=30, so the login page can still show the previous
    // announcements for up to about 40 seconds after a save.
    await queryClient.invalidateQueries({ queryKey: ['SIGNIN_ANNOUNCEMENTS'] });
  };

  // A null id creates; otherwise it replaces that row.
  const save = async ({ id, values }: SaveParams) => {
    const saved =
      id === null
        ? await client.post<SigninAnnouncementSelect>('/signin-panel', values)
        : await client.put<SigninAnnouncementSelect>(`/signin-panel/${id}`, values);
    await refresh();
    return saved;
  };

  const remove = async (id: number) => {
    await client.delete(`/signin-panel/${id}`);
    await refresh();
  };

  return { announcements: data ?? [], isLoading, save, remove };
};
